// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/shared/protocol.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * WebSocket Protocol Schema - Phase 3 Standardization
 * 
 * Defines type-safe union types for all WebSocket messages in the AYNI-2 system.
 * Replaces `any` casts with proper type guards and discriminated unions.
 */

import type { MediaState, CanvasState } from './types';

// Base message type - all messages extend this
export interface BaseMessage {
  timestamp: number;
}

// Message type discriminator for union types
export const MessageTypes = {
  // Connection and lifecycle
  CONNECTION: 'connection',
  CONNECTION_HELLO: 'connection_hello',
  PING: 'ping',
  PONG: 'pong',
  
  // Authentication
  AUTH: 'auth',
  AUTH_CHALLENGE: 'auth_challenge',
  AUTH_SUCCESS: 'auth_success',
  AUTH_ERROR: 'auth_error',
  
  // Control messages
  CONTROL: 'control',
  
  // State synchronization
  STATE: 'state',
  STATE_REQUEST: 'state_request',
  
  // Media operations
  MEDIA: 'media',
  MEDIA_REQUEST: 'media_request',
  MEDIA_INFO: 'media_info',
  
  // Kiosk streaming
  KIOSK_REGISTER: 'kiosk_register',
  KIOSK_STATUS: 'kiosk_status',
  STREAM_DATA: 'stream_data',
  STREAM_METADATA: 'stream_metadata',
  QUALITY_REQUEST: 'quality_request',
  STREAM_STATS: 'stream_stats',
  REQUEST_STREAM_METADATA: 'request_stream_metadata',
  
  // Playlist operations
  PLAYLIST_CONTROL: 'playlist_control',
  PLAYLIST_STATE: 'playlist_state',
  PLAYLIST_STATE_REQUEST: 'playlist_state_request',
  
  // Asset operations
  ASSET_REQUEST_CATALOG: 'asset_request_catalog',
  ASSET_CATALOG: 'asset_catalog',
  ASSET_SELECT: 'asset_select',
  
  // Error handling
  ERROR: 'error',
  
  // API commands
  API_MEDIA_LOAD: 'api_media_load',
  API_MEDIA_PLAY: 'api_media_play',
  API_MEDIA_PAUSE: 'api_media_pause',
  API_MEDIA_STOP: 'api_media_stop',
  API_MEDIA_SEEK: 'api_media_seek',
  API_CANVAS_MOVE: 'api_canvas_move',
  API_CANVAS_ZOOM: 'api_canvas_zoom',
  API_ROTATION_SET: 'api_rotation_set',
  API_STATE_REQUEST: 'api_state_request',
  API_STATE_MEDIA: 'api_state_media',
  API_STATE_CANVAS: 'api_state_canvas',

  // AI agent operations
  AGENT_COMMAND: 'agent_command',
  AGENT_COMMAND_RESULT: 'agent_command_result',
  AGENT_STATE_REQUEST: 'agent_state_request',
  AGENT_STATE: 'agent_state',
  AGENT_CATALOG_SEARCH: 'agent_catalog_search',
  AGENT_CAPABILITIES: 'agent_capabilities'
} as const;

export type MessageType = typeof MessageTypes[keyof typeof MessageTypes];

// Base WebSocket message - replaces the existing WebSocketMessage interface
export interface WebSocketMessage<T = unknown> extends BaseMessage {
  type: MessageType;
  data: T;
}

// Connection messages
export interface ConnectionMessage extends WebSocketMessage {
  type: typeof MessageTypes.CONNECTION;
  data: {
    status: 'connected' | 'disconnected';
    clientId?: string;
    reason?: string;
  };
}

export interface ConnectionHelloMessage extends WebSocketMessage {
  type: typeof MessageTypes.CONNECTION_HELLO;
  data: {
    clientId: string;
    role: 'presenter' | 'kiosk' | 'viewer';
    capabilities?: string[];
    timestamp: number;
  };
}

// Ping/Pong messages
export interface PingMessage extends WebSocketMessage {
  type: typeof MessageTypes.PING;
  data: {
    pingTimestamp: number;
  };
}

export interface PongMessage extends WebSocketMessage {
  type: typeof MessageTypes.PONG;
  data: {
    pingTimestamp: number;
    pongTimestamp: number;
  };
}

// Authentication messages
export interface AuthMessage extends WebSocketMessage {
  type: typeof MessageTypes.AUTH;
  data: {
    token?: string;
    username?: string;
    role?: string;
  };
}

export interface AuthChallengeMessage extends WebSocketMessage {
  type: typeof MessageTypes.AUTH_CHALLENGE;
  data: {
    challenge: string;
  };
}

export interface AuthSuccessMessage extends WebSocketMessage {
  type: typeof MessageTypes.AUTH_SUCCESS;
  data: {
    token: string;
    userId: string;
    role: string;
    permissions: string[];
  };
}

export interface AuthErrorMessage extends WebSocketMessage {
  type: typeof MessageTypes.AUTH_ERROR;
  data: {
    error: string;
    code: string;
  };
}

// Control messages
export interface ControlMessage extends WebSocketMessage {
  type: typeof MessageTypes.CONTROL;
  data: {
    action: string;
    parameter?: unknown;
    clientId?: string;
    broadcast?: boolean;
  };
}

// State messages
export interface StateMessage extends WebSocketMessage {
  type: typeof MessageTypes.STATE;
  data: {
    type: 'update' | 'request' | 'response';
    state: Partial<MediaState> | Partial<CanvasState>;
    scope?: 'media' | 'canvas' | 'playlist' | 'all';
  };
}

export interface StateRequestMessage extends WebSocketMessage {
  type: typeof MessageTypes.STATE_REQUEST;
  data: {
    scope: 'media' | 'canvas' | 'playlist' | 'all';
    clientId: string;
  };
}

// Media messages
export interface MediaMessage extends WebSocketMessage {
  type: typeof MessageTypes.MEDIA;
  data: {
    action: 'load' | 'play' | 'pause' | 'stop' | 'seek' | 'volume' | 'speed';
    parameter?: unknown;
    currentTime?: number;
    duration?: number;
    isPlaying?: boolean;
  };
}

export interface MediaRequestMessage extends WebSocketMessage {
  type: typeof MessageTypes.MEDIA_REQUEST;
  data: {
    action: 'info' | 'thumbnail' | 'metadata';
    filePath?: string;
  };
}

export interface MediaInfoMessage extends WebSocketMessage {
  type: typeof MessageTypes.MEDIA_INFO;
  data: {
    mediaInfo: unknown;
  };
}

// Error messages
export interface ErrorMessage extends WebSocketMessage {
  type: typeof MessageTypes.ERROR;
  data: {
    code: ErrorCode;
    message: string;
    details?: unknown;
    timestamp: number;
  };
}

// Kiosk streaming messages
export interface KioskRegisterMessage extends WebSocketMessage {
  type: typeof MessageTypes.KIOSK_REGISTER;
  data: {
    clientId: string;
    capabilities: string[];
    requestedQuality?: string;
  };
}

export interface KioskStatusMessage extends WebSocketMessage {
  type: typeof MessageTypes.KIOSK_STATUS;
  data: {
    clientId: string;
    status: 'connected' | 'disconnected' | 'streaming';
    stats?: {
      fps: number;
      bitrate: number;
      latency: number;
    };
  };
}

export interface StreamDataMessage extends WebSocketMessage {
  type: typeof MessageTypes.STREAM_DATA;
  data: {
    clientId: string;
    frame: Uint8Array;
    timestamp: number;
    metadata?: unknown;
  };
}

export interface StreamMetadataMessage extends WebSocketMessage {
  type: typeof MessageTypes.STREAM_METADATA;
  data: {
    clientId: string;
    metadata: {
      fps: number;
      bitrate: number;
      resolution?: { width: number; height: number };
    };
  };
}

export interface QualityRequestMessage extends WebSocketMessage {
  type: typeof MessageTypes.QUALITY_REQUEST;
  data: {
    clientId: string;
    quality: string;
    adaptive?: boolean;
  };
}

export interface StreamStatsMessage extends WebSocketMessage {
  type: typeof MessageTypes.STREAM_STATS;
  data: {
    clientId: string;
    stats: {
      fps: number;
      bitrate: number;
      droppedFrames?: number;
      latency?: number;
    };
  };
}

export interface RequestStreamMetadataMessage extends WebSocketMessage {
  type: typeof MessageTypes.REQUEST_STREAM_METADATA;
  data: {
    clientId: string;
  };
}

// Playlist messages
export interface PlaylistControlMessage extends WebSocketMessage {
  type: typeof MessageTypes.PLAYLIST_CONTROL;
  data: {
    command: 'play' | 'pause' | 'next' | 'previous' | 'setIndex' | 'toggleAutoplay';
    parameter?: unknown;
    playlistId?: string;
    itemIndex?: number;
  };
}

export interface PlaylistStateMessage extends WebSocketMessage {
  type: typeof MessageTypes.PLAYLIST_STATE;
  data: {
    currentPlaylistId?: string;
    currentItemIndex?: number;
    autoplay?: boolean;
    playlist?: unknown;
  };
}

export interface PlaylistStateRequestMessage extends WebSocketMessage {
  type: typeof MessageTypes.PLAYLIST_STATE_REQUEST;
  data: {
    playlistId?: string;
  };
}

// Asset messages
export interface AssetRequestCatalogMessage extends WebSocketMessage {
  type: typeof MessageTypes.ASSET_REQUEST_CATALOG;
  data: {
    scope?: 'all' | 'category' | 'recent';
    category?: string;
  };
}

export interface AssetCatalogMessage extends WebSocketMessage {
  type: typeof MessageTypes.ASSET_CATALOG;
  data: {
    catalog: unknown;
    timestamp: number;
  };
}

export interface AssetSelectMessage extends WebSocketMessage {
  type: typeof MessageTypes.ASSET_SELECT;
  data: {
    assetId: string;
    source: 'local' | 'remote';
    metadata?: unknown;
  };
}

// API messages
export interface ApiMediaLoadMessage extends WebSocketMessage {
  type: typeof MessageTypes.API_MEDIA_LOAD;
  data: {
    filePath: string;
    options?: {
      autoplay?: boolean;
      startTime?: number;
    };
  };
}

export interface ApiMediaPlayMessage extends WebSocketMessage {
  type: typeof MessageTypes.API_MEDIA_PLAY;
  data: Record<string, unknown>;
}

export interface ApiMediaPauseMessage extends WebSocketMessage {
  type: typeof MessageTypes.API_MEDIA_PAUSE;
  data: Record<string, unknown>;
}

export interface ApiMediaStopMessage extends WebSocketMessage {
  type: typeof MessageTypes.API_MEDIA_STOP;
  data: Record<string, unknown>;
}

export interface ApiMediaSeekMessage extends WebSocketMessage {
  type: typeof MessageTypes.API_MEDIA_SEEK;
  data: {
    time: number;
  };
}

export interface ApiCanvasMoveMessage extends WebSocketMessage {
  type: typeof MessageTypes.API_CANVAS_MOVE;
  data: {
    deltaX: number;
    deltaY: number;
  };
}

export interface ApiCanvasZoomMessage extends WebSocketMessage {
  type: typeof MessageTypes.API_CANVAS_ZOOM;
  data: {
    zoomFactor: number;
    centerX?: number;
    centerY?: number;
  };
}

export interface ApiRotationSetMessage extends WebSocketMessage {
  type: typeof MessageTypes.API_ROTATION_SET;
  data: {
    enabled?: boolean;
    rotation_enabled?: boolean;
    speed?: number;
    rotation_speed?: number;
    requestId?: string;
    origin?: string;
  };
}

export interface ApiStateRequestMessage extends WebSocketMessage {
  type: typeof MessageTypes.API_STATE_REQUEST;
  data: {
    scope: 'media' | 'canvas' | 'playlist' | 'all';
  };
}

export interface ApiStateMediaMessage extends WebSocketMessage {
  type: typeof MessageTypes.API_STATE_MEDIA;
  data: MediaState;
}

export interface ApiStateCanvasMessage extends WebSocketMessage {
  type: typeof MessageTypes.API_STATE_CANVAS;
  data: CanvasState;
}

// AI agent messages
export type AgentCommand =
  | { type: 'catalog.search'; query: string; categories?: string[]; limit?: number }
  | { type: 'dataset.load'; datasetId: string; sliceId?: number }
  | { type: 'view.goto'; longitude: number; latitude: number; zoom?: number }
  | { type: 'view.zoom'; zoom: number }
  | { type: 'view.rotation'; enabled: boolean; speed?: number }
  | { type: 'media.play' }
  | { type: 'media.pause' }
  | { type: 'media.stop' }
  | { type: 'media.seek'; time: number }
  | { type: 'media.seekDate'; date: string }
  | { type: 'media.speed'; speed: number }
  | { type: 'sphere.slice'; count: 1 | 2 | 3 | 4 }
  | { type: 'state.describe'; includeCatalogContext?: boolean }
  // Value-encoded (data-luma) presentation and analysis. Every one of these
  // is inert unless the active release publishes `valueEncoding`; the
  // analysis commands answer `{ available: false, reason }` rather than
  // guessing, so a caller can never present an uncomputed number as fact.
  | { type: 'luma.describe' }
  | { type: 'luma.palette'; palette: string }
  | { type: 'luma.stretch'; lo?: number; hi?: number; reset?: boolean }
  | { type: 'luma.probe'; lat: number; lon: number }
  | { type: 'luma.probeMany'; points: Array<{ lat: number; lon: number }> }
  | { type: 'luma.stats'; bounds?: AgentLatLonBounds; threshold?: number; includeHistogram?: boolean }
  | { type: 'luma.extremum'; kind?: 'max' | 'min'; bounds?: AgentLatLonBounds }
  | {
      type: 'luma.transect';
      from: { lat: number; lon: number };
      to: { lat: number; lon: number };
      samples?: number;
    }
  | { type: 'luma.zonal'; bounds?: AgentLatLonBounds; maxRows?: number }
  // Measurement pins: places whose values are followed as the dataset plays
  // (AYNI 3D's measurement pins, AYNI XR's Data tab). `sliceIndex` and `ids`
  // are not offered to the model: AYNI 3D's kiosk scope sets them, so a
  // kiosk's pins are checked against its own slice and it clears only its own.
  | { type: 'pins.place'; points: Array<{ lat: number; lon: number }>; replace?: boolean; sliceIndex?: number }
  | { type: 'pins.read'; sliceIndex?: number }
  | { type: 'pins.clear'; ids?: string[] }
  | { type: 'pins.extremes'; sliceIndex?: number }
  // Research, general and education commands for AYNI XR's Research and
  // Education environments (docs/AI_CHATBOT_RESEARCH_EDUCATION_CONTRACT.md).
  // Declared off in AYNI 3D and 2D; a host that carries them out opts in.
  // Each answers an AgentToolOutcome.
  | { type: 'stats.trend'; pin: string }
  | { type: 'stats.emergence'; pin: string; baseline?: AgentYearSpan; noise?: number }
  | { type: 'stats.correlation'; pinA: string; pinB: string }
  | { type: 'baseline.hold'; frame?: 'current'; period?: 'default' | AgentYearSpan }
  | { type: 'baseline.status' }
  | { type: 'baseline.clear' }
  | { type: 'change.show'; mode: 'off' | 'difference' | 'percent' | 'sigma' }
  | {
      type: 'compare.run';
      source: 'overlay' | 'globe';
      id?: string;
      pairing?: 'time' | 'shown';
      mode?: 'difference' | 'percent';
      bounds?: AgentLatLonBounds;
    }
  | {
      type: 'threshold.area';
      mode: 'value' | 'difference' | 'sigma';
      comparator: 'above' | 'below';
      threshold: number;
      bounds?: AgentLatLonBounds;
    }
  | { type: 'hovmoller.summary'; from?: { lat: number; lon: number }; to?: { lat: number; lon: number } }
  | { type: 'methods.get' }
  | { type: 'figure.open'; kind?: 'globe' | 'equal-earth' }
  | { type: 'bundle.open' }
  | { type: 'cite.view'; format?: 'text' | 'bibtex' | 'ris' }
  | { type: 'lesson.find'; query?: string; grade?: number; standard?: string; unit?: string; limit?: number }
  | { type: 'lesson.open'; slug: string }
  | { type: 'lesson.context' }
  | { type: 'lesson.hint'; step?: number }
  | { type: 'misconception.check'; claim: string }
  | { type: 'explain.value'; lat?: number; lon?: number }
  // DSA 1.8 vector layers: the records a dataset draws itself (points such as
  // earthquakes, lines, areas, tracks), read by their fields, never from
  // colours. Declared off in AYNI 3D and 2D until their executors carry it
  // out; AYNI XR opts in. Answers an AgentToolOutcome.
  | {
      type: 'features.query';
      layer?: string;
      when?: 'shown' | 'all';
      from?: string;
      to?: string;
      bounds?: AgentLatLonBounds;
      sortBy?: string;
      order?: 'desc' | 'asc';
      where?: AgentFeatureFilter[];
      limit?: number;
    }
  // The Satellites layer and the catalog as one graph (AYNI XR: DSA 1.10
  // `platforms` and the stream host's `sources.json`), read from the layer,
  // never from a copy. Declared off in AYNI 3D and 2D, which have no
  // Satellites layer; AYNI XR opts in. Each answers an AgentToolOutcome.
  // `date`: the satellites of that period (ISO), else the frame on screen.
  | { type: 'satellites.sources'; date?: string }
  // `satellite`: a name ("Sentinel-1" matches each Sentinel-1) or a NORAD id.
  | { type: 'satellites.datasets'; satellite: string }
  // Turns the layer on (`on: false` off), selects `norad`, sets the field of view.
  | { type: 'satellites.show'; on?: boolean; norad?: number; fov?: boolean }
  | { type: 'satellites.passes' }
  // The space events; `open` (an event id) opens one on the globe.
  | { type: 'satellites.events'; open?: string; limit?: number }
  // AYNI XR's Solar System mode: an orrery of the Sun, the planets, the Moon,
  // Ceres and Pluto where they are on its clock's date, and a visit to each
  // (its globe and datasets). Declared off in AYNI 3D and 2D, which have no
  // Solar System mode; AYNI XR opts in. Each answers an AgentToolOutcome.
  // Opens the orrery (from a body, back to it).
  | { type: 'solar.open' }
  // Visits `body`: from the orrery or the globe it goes there; from another
  // body it flies (or fades, as the visitor chose) there.
  | { type: 'solar.visit'; body: AgentSolarBody }
  // The orrery's layout and clock, any of them: true distances or true sizes;
  // play or pause; how fast time runs (a day, a week, a month or a year a
  // second); back to today.
  | { type: 'solar.view'; layout?: 'distances' | 'sizes'; play?: boolean; speed?: AgentSolarSpeed; today?: boolean }
  // Leaves the mode: back to the globe and the dataset it began with.
  | { type: 'solar.close' }
  // Khipus, the guided stories of steps on the sphere (docs/KHIPUS.md); off
  // by default, an app that plays them opts in (AYNI 3D). The list is answered
  // in main, the rest by the Khipu player. `step` is 1-based.
  | { type: 'khipu.list'; query?: string; limit?: number }
  | { type: 'khipu.open'; khipuId: string; step?: number }
  | { type: 'khipu.step'; to: 'next' | 'previous' | number }
  | { type: 'khipu.close' };

/** A Khipu in the library, as the chatbot's khipu.list answers it. */
export interface AgentKhipuEntry {
  id: string;
  title: string;
  steps: number;
  description?: string;
}

/** One condition on a vector feature's field (`features.query`): at least one of min, max or equals. */
export interface AgentFeatureFilter {
  field: string;
  min?: number;
  max?: number;
  equals?: string | number | boolean;
}

/** A span of whole years, inclusive (a baseline period). */
export interface AgentYearSpan {
  from: number;
  to: number;
}

/**
 * What a research, general or education command answers, in
 * `AgentForwarderResult.result` (with `success: true`):
 * - `ok`: numbers and labels in `values`, with the method and every caution
 *   that applies, in the module's own words (English; the model translates);
 * - `pending`: work under way, e.g. a baseline period's frames being read;
 * - `unavailable`: the module refused, `reason` is its own code;
 * - `locked`: the plan, the class's lent tools, the role or a meeting
 *   audience does not allow it. The executor words `message` for the role:
 *   never a plan, price or upgrade to a student.
 * `teacher` carries teacher-only material; the chatbot engine removes it
 * before the model sees it in any mode but teacher.
 */
/** The bodies of AYNI XR's Solar System mode (solar.visit). */
export const AGENT_SOLAR_BODIES = ['sun', 'mercury', 'venus', 'earth', 'moon', 'mars', 'ceres', 'jupiter', 'saturn', 'uranus', 'neptune', 'pluto'] as const;
export type AgentSolarBody = (typeof AGENT_SOLAR_BODIES)[number];
/** How fast the Solar System's clock runs: a day, a week, a month or a year a second. */
export const AGENT_SOLAR_SPEEDS = ['day', 'week', 'month', 'year'] as const;
export type AgentSolarSpeed = (typeof AGENT_SOLAR_SPEEDS)[number];

export type AgentToolOutcome =
  | {
      status: 'ok';
      values: Record<string, unknown>;
      method: string;
      cautions: AgentCaution[];
      references?: string[];
      teacher?: Record<string, unknown>;
    }
  | { status: 'pending'; progress: { done: number; total: number; unit: string }; message: string }
  | { status: 'unavailable'; reason: string; message: string }
  | { status: 'locked'; lock: AgentLockReason; message: string };

export interface AgentCaution {
  code: string;
  text: string;
}

export type AgentLockReason = 'plan' | 'class' | 'role' | 'meeting';

/** North/south/west/east degrees; `w > e` crosses the antimeridian. */
export interface AgentLatLonBounds {
  n: number;
  s: number;
  w: number;
  e: number;
}

export interface AgentCatalogResult {
  id: string;
  title: string;
  description?: string;
  categories: string[];
  keywords?: string[];
  dataProductType?: string;
  thumbnail?: string;
  links?: {
    source?: string;
    documentation?: string;
  };
}

export interface AgentCapability {
  name: AgentCommand['type'];
  enabled: boolean;
  description: string;
  inputSchema?: unknown;
  unavailableReason?: string;
}

export interface AgentObservation {
  currentDataset?: {
    id: string;
    title: string;
    description?: string;
    categories: string[];
    keywords?: string[];
    links?: Record<string, string>;
    ai?: {
      contextFile?: string;
      persona?: string;
    };
    /** The body the data shows when it is not the Earth (DSA 1.9 celestialBody: "sun", "mars"…). */
    body?: string;
  };
  media: MediaState;
  canvas: CanvasState;
  capabilities: AgentCapability[];
  /**
   * The dataset's time range and the date its time label shows now (ISO), when
   * it has one: the same mapping the label uses, so both always agree.
   */
  timeline?: { start: string; end: string; now: string | null };
  /** The Khipu playing on the sphere, if any: its step (1-based) and the words the step shows. */
  khipu?: { id: string; title: string; step: number; count: number; stepTitle: string; words?: string; playing: boolean };
}

export interface AgentCommandResult {
  requestId: string;
  success: boolean;
  result?: unknown;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
  observation?: AgentObservation;
}

/**
 * Result of forwarding a control command to the authoritative renderer.
 * The renderer's AgentControlService produces this shape. It lives here, not
 * with the main-process forwarder, so the chatbot's engine compiles in hosts
 * that have no main process (AYNI XR runs it in the page).
 */
export interface AgentForwarderResult {
  success: boolean;
  result?: unknown;
  error?: { code: string; message: string; details?: unknown };
  observation?: AgentCommandResult['observation'];
}

export interface AgentCommandMessage extends WebSocketMessage {
  type: typeof MessageTypes.AGENT_COMMAND;
  data: {
    requestId: string;
    command: AgentCommand;
  };
}

export interface AgentCommandResultMessage extends WebSocketMessage {
  type: typeof MessageTypes.AGENT_COMMAND_RESULT;
  data: AgentCommandResult;
}

export interface AgentStateRequestMessage extends WebSocketMessage {
  type: typeof MessageTypes.AGENT_STATE_REQUEST;
  data: {
    requestId: string;
    includeCatalogContext?: boolean;
  };
}

export interface AgentStateMessage extends WebSocketMessage {
  type: typeof MessageTypes.AGENT_STATE;
  data: {
    requestId?: string;
    observation: AgentObservation;
  };
}

export interface AgentCatalogSearchMessage extends WebSocketMessage {
  type: typeof MessageTypes.AGENT_CATALOG_SEARCH;
  data: {
    requestId: string;
    query: string;
    categories?: string[];
    limit?: number;
  };
}

export interface AgentCapabilitiesMessage extends WebSocketMessage {
  type: typeof MessageTypes.AGENT_CAPABILITIES;
  data: {
    requestId?: string;
    capabilities?: AgentCapability[];
  };
}

// Error codes for standardized error handling
export const ErrorCodes = {
  // Authentication errors
  AUTH_FAILED: 'AUTH_FAILED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  INVALID_ROLE: 'INVALID_ROLE',
  
  // Connection errors
  CONNECTION_FAILED: 'CONNECTION_FAILED',
  CONNECTION_TIMEOUT: 'CONNECTION_TIMEOUT',
  CONNECTION_LOST: 'CONNECTION_LOST',
  
  // Media errors
  MEDIA_NOT_FOUND: 'MEDIA_NOT_FOUND',
  MEDIA_UNSUPPORTED: 'MEDIA_UNSUPPORTED',
  MEDIA_LOAD_FAILED: 'MEDIA_LOAD_FAILED',
  
  // State errors
  STATE_SYNC_FAILED: 'STATE_SYNC_FAILED',
  INVALID_STATE: 'INVALID_STATE',
  
  // General errors
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  INVALID_REQUEST: 'INVALID_REQUEST',
  INTERNAL_ERROR: 'INTERNAL_ERROR'
} as const;

export type ErrorCode = typeof ErrorCodes[keyof typeof ErrorCodes];

// Main union type - discriminated union for all message types
export type ProtocolMessage =
  | ConnectionMessage
  | ConnectionHelloMessage
  | PingMessage
  | PongMessage
  | AuthMessage
  | AuthChallengeMessage
  | AuthSuccessMessage
  | AuthErrorMessage
  | ControlMessage
  | StateMessage
  | StateRequestMessage
  | MediaMessage
  | MediaRequestMessage
  | MediaInfoMessage
  | KioskRegisterMessage
  | KioskStatusMessage
  | StreamDataMessage
  | StreamMetadataMessage
  | QualityRequestMessage
  | StreamStatsMessage
  | RequestStreamMetadataMessage
  | PlaylistControlMessage
  | PlaylistStateMessage
  | PlaylistStateRequestMessage
  | AssetRequestCatalogMessage
  | AssetCatalogMessage
  | AssetSelectMessage
  | ApiMediaLoadMessage
  | ApiMediaPlayMessage
  | ApiMediaPauseMessage
  | ApiMediaStopMessage
  | ApiMediaSeekMessage
  | ApiCanvasMoveMessage
  | ApiCanvasZoomMessage
  | ApiRotationSetMessage
  | ApiStateRequestMessage
  | ApiStateMediaMessage
  | ApiStateCanvasMessage
  | AgentCommandMessage
  | AgentCommandResultMessage
  | AgentStateRequestMessage
  | AgentStateMessage
  | AgentCatalogSearchMessage
  | AgentCapabilitiesMessage
  | ErrorMessage;

// Type guards for runtime type checking
export function isConnectionMessage(message: WebSocketMessage): message is ConnectionMessage {
  return message.type === MessageTypes.CONNECTION;
}

export function isPingMessage(message: WebSocketMessage): message is PingMessage {
  return message.type === MessageTypes.PING;
}

export function isPongMessage(message: WebSocketMessage): message is PongMessage {
  return message.type === MessageTypes.PONG;
}

export function isAuthMessage(message: WebSocketMessage): message is AuthMessage {
  return message.type === MessageTypes.AUTH;
}

export function isAuthSuccessMessage(message: WebSocketMessage): message is AuthSuccessMessage {
  return message.type === MessageTypes.AUTH_SUCCESS;
}

export function isAuthErrorMessage(message: WebSocketMessage): message is AuthErrorMessage {
  return message.type === MessageTypes.AUTH_ERROR;
}

export function isControlMessage(message: WebSocketMessage): message is ControlMessage {
  return message.type === MessageTypes.CONTROL;
}

export function isStateMessage(message: WebSocketMessage): message is StateMessage {
  return message.type === MessageTypes.STATE;
}

export function isStateRequestMessage(message: WebSocketMessage): message is StateRequestMessage {
  return message.type === MessageTypes.STATE_REQUEST;
}

export function isMediaMessage(message: WebSocketMessage): message is MediaMessage {
  return message.type === MessageTypes.MEDIA;
}

export function isMediaRequestMessage(message: WebSocketMessage): message is MediaRequestMessage {
  return message.type === MessageTypes.MEDIA_REQUEST;
}

export function isErrorMessage(message: WebSocketMessage): message is ErrorMessage {
  return message.type === MessageTypes.ERROR;
}

export function isAgentCommandMessage(message: WebSocketMessage): message is AgentCommandMessage {
  return message.type === MessageTypes.AGENT_COMMAND;
}

export function isAgentCommandResultMessage(message: WebSocketMessage): message is AgentCommandResultMessage {
  return message.type === MessageTypes.AGENT_COMMAND_RESULT;
}

export function isAgentStateRequestMessage(message: WebSocketMessage): message is AgentStateRequestMessage {
  return message.type === MessageTypes.AGENT_STATE_REQUEST;
}

export function isAgentCatalogSearchMessage(message: WebSocketMessage): message is AgentCatalogSearchMessage {
  return message.type === MessageTypes.AGENT_CATALOG_SEARCH;
}

export function isAgentCapabilitiesMessage(message: WebSocketMessage): message is AgentCapabilitiesMessage {
  return message.type === MessageTypes.AGENT_CAPABILITIES;
}

// Utility function to create typed messages
export function createMessage<T extends ProtocolMessage>(type: T['type'] & MessageType, data: T['data']): T {
  return {
    type,
    data,
    timestamp: Date.now()
  } as T;
}

// Response helpers for consistent error handling
export function createErrorMessage(code: ErrorCode, message: string, details?: unknown): ErrorMessage {
  return {
    type: MessageTypes.ERROR,
    data: {
      code,
      message,
      details,
      timestamp: Date.now()
    },
    timestamp: Date.now()
  };
}

export function createSuccessMessage(type: MessageType, data: unknown): WebSocketMessage {
  return {
    type,
    data,
    timestamp: Date.now()
  };
}
